import { useLocation } from "react-router";

import { RevenueStripeSafetyNotice } from "@/components/environment/revenueStripeSafety";
import { buildAdminRevenueScopedPath } from "@/utils/adminRevenueAccessScope";
import { AdminVNextShell } from "@/vnext/app-shell/AdminVNextShell";
import { useWorkspaceChromeLogging } from "@/vnext/routes/useWorkspaceChromeLogging";
import { WorkspaceTabNav } from "@/vnext/shared/workspace/WorkspaceTabNav";
import "@/vnext/shared/workspace/workspaceChrome.css";

interface RevenueWorkspaceChromeProps {
  actions?: React.ReactNode;
  children: React.ReactNode;
  subtitle: string;
  title: string;
}

const REVENUE_TABS = [
  {
    label: "Revenue offers and onboarding",
    path: "/revenue",
  },
  {
    label: "Billing overview",
    path: "/revenue/billing",
  },
  {
    label: "Subscription tools",
    path: "/subscription",
  },
  {
    label: "Coupons",
    path: "/revenue/coupons",
  },
  {
    label: "Affiliates",
    path: "/revenue/affiliates",
  },
] as const;

const getActivePath = (pathname: string) => {
  if (
    pathname.startsWith("/revenue/billing") ||
    pathname.startsWith("/quickbooks-trials")
  ) {
    return "/revenue/billing";
  }
  if (pathname.startsWith("/subscription")) {
    return "/subscription";
  }
  if (pathname.startsWith("/revenue/coupons")) {
    return "/revenue/coupons";
  }
  if (pathname.startsWith("/revenue/affiliates")) {
    return "/revenue/affiliates";
  }

  return "/revenue";
};

export function RevenueWorkspaceChrome({
  actions,
  children,
  subtitle,
  title,
}: RevenueWorkspaceChromeProps) {
  const location = useLocation();
  const activePath = getActivePath(location.pathname);
  const logTabClick = useWorkspaceChromeLogging({
    activePath,
    componentName: "RevenueWorkspaceChrome",
    pathname: location.pathname,
    tabs: REVENUE_TABS,
    workspaceLabel: "revenue",
  });

  return (
    <AdminVNextShell
      actions={actions}
      breadcrumbs={[
        { label: "Dashboard", path: "/dashboard" },
        { label: "Revenue and billing" },
        { label: title },
      ]}
      subtitle={subtitle}
      title={title}
    >
      <div className="admin-workspace-page">
        <WorkspaceTabNav
          activePath={activePath}
          ariaLabel="Revenue and billing sections"
          buildPath={(tab) =>
            buildAdminRevenueScopedPath(tab.path, location.search)
          }
          onTabClick={logTabClick}
          tabs={REVENUE_TABS}
        />
        <RevenueStripeSafetyNotice />
        {children}
      </div>
    </AdminVNextShell>
  );
}
